/**
 * The words a DRAWING's comment box uses.
 *
 * Deliberately its own file rather than an addition to `tradeIdeaVocab.ts` — see
 * the warning at the top of `legTypes.ts`. The workbench now has three things
 * that let a trader write a sentence, and they are kept apart on purpose:
 *
 *  - a trade idea's levels  → `tradeIdeaVocab.ts` (entry / stop / target)
 *  - the legacy buy/sell marker → `legTypes.ts`
 *  - a plain drawing        → here
 *
 * A drawing is not a step in a sequence and has no direction, so it asks one
 * open question rather than three pointed ones, and its box carries no button
 * to advance with.
 */

/**
 * The overlays that take a comment.
 *
 * What is NOT here, and why:
 *  - `tradeMark` — a trade idea's levels have their own box, with their own
 *    questions and the flow that advances between them.
 *  - `buyEntry` / `sellEntry` / `exitPoint` / `levelLine` — the legacy marker
 *    tool, which has carried its own composer since before this existed.
 *  - text and callout notes — they are not overlays at all (they are React
 *    `WorkbenchNote` state), and a comment on a comment says nothing.
 *
 * `rayLine` and `straightLine` are here even though no rail tool draws them:
 * `handleExtend` swaps a `segment` into one as it is extended, and an extended
 * trend line is still a trend line.
 */
export const NOTABLE_DRAWINGS = new Set([
  'segment',
  'rayLine',
  'straightLine',
  'arrow',
  'horizontalStraightLine',
  'verticalStraightLine',
  'parallelStraightLine',
  'fibRetracement',
  'rectangle',
])

/** True when this overlay is a drawing a trader may comment on. */
export function isNotableDrawing(name: string | undefined | null): boolean {
  return typeof name === 'string' && NOTABLE_DRAWINGS.has(name)
}

/**
 * What the box says it is attached to — the trader's name for the shape, not
 * the engine's. "parallelStraightLine" is what klinecharts calls it; "this
 * channel" is what the rail's tooltip promised when they drew it.
 */
export const DRAWING_SUBJECT: Record<string, string> = {
  segment: 'this trend line',
  rayLine: 'this trend line',
  straightLine: 'this trend line',
  arrow: 'this arrow',
  horizontalStraightLine: 'this horizontal level',
  verticalStraightLine: 'this vertical marker',
  parallelStraightLine: 'this channel',
  fibRetracement: 'this Fibonacci retracement',
  rectangle: 'this zone',
}

/**
 * The box's header. Names the exact shape it is attached to, so it never reads
 * as a generic "talk to the AI" prompt — the same rule `noteHeader` follows for
 * a trade level.
 */
export function drawingNoteHeader(name: string): string {
  return `Add more context about ${DRAWING_SUBJECT[name] ?? 'this drawing'}`
}

/**
 * The question inside the field. One question for every shape, unlike the three
 * a trade idea asks: a trend line, a zone and a Fibonacci are all answering the
 * same thing — what the trader saw that made them draw it. Asking it nine
 * slightly different ways would be nine chances for the wording to drift.
 */
export const DRAWING_QUESTION = 'What does this show us?'
